"use client"

import { useSyncExternalStore, useCallback } from "react"
import {
  type SectionSlug,
  type SectionDataMap,
  type CompletionStatus,
  SECTION_CONFIGS,
  SECTION_ORDER,
  type IdentityData,
  type PeopleData,
  type OfferingsData,
  type AudienceData,
  type BrandData,
  type CompetitionData,
  type TechnologyData,
  type MarketingData,
  type SalesData,
  type OperationsData,
  type FinanceData,
  type StrategyData,
} from "@/lib/types/sections"

// Re-export Product and Service for backwards compatibility
export type { Product, Service } from "@/lib/types/sections"

// ============================================
// SECTION STATE
// ============================================
export interface SectionState<T = unknown> {
  id: string
  slug: SectionSlug
  label: string
  description: string
  icon: string
  data: T
  completionStatus: CompletionStatus
  completionPercent: number
  displayOrder: number
  estimatedTime: string
  lastUpdated?: string
}

// ============================================
// DEFAULT DATA FOR EACH SECTION
// ============================================
const defaultSectionData: SectionDataMap = {
  identity: {},
  people: { team_members: [], advisors: [], partners: [] },
  offerings: { products: [], services: [], programs: [], bundles: [] },
  audience: { segments: [], journeys: [] },
  brand: { visual: {}, messaging: {}, assets: [] },
  competition: { competitors: [], alternatives: [], swot: {} },
  technology: { systems: [], data_sources: [], integration_priorities: [] },
  marketing: { channels: [], funnels: [], budget_summary: {} },
  sales: { pipelines: [], pricing_overview: {}, key_objections: [] },
  operations: { core_processes: [], vendors: [] },
  finance: { pricing_summary: {}, cost_structure: {}, unit_economics: {}, funding: {} },
  strategy: {
    strategic_vision: {},
    goals: [],
    roadmaps: [],
    execution_plans: [],
    business_plans: [],
    marketing_plans: [],
    sales_plans: [],
    operational_plans: [],
    strategic_initiatives: [],
    experiments: [],
    quarterly_reviews: [],
    metadata: {},
  },
}

// ============================================
// COMPLETION CALCULATORS
// ============================================
function calculateIdentityCompletion(data: IdentityData): number {
  const fields = ["legal_name", "display_name", "mission", "vision", "values", "story"]
  const filled = fields.filter((f) => {
    const val = data[f as keyof IdentityData]
    if (Array.isArray(val)) return val.length > 0
    return val && String(val).trim() !== ""
  }).length
  return Math.round((filled / fields.length) * 100)
}

function calculatePeopleCompletion(data: PeopleData): number {
  const hasTeam = (data.team_members?.length || 0) > 0
  const hasAdvisors = (data.advisors?.length || 0) > 0
  const hasPartners = (data.partners?.length || 0) > 0
  return (hasTeam ? 60 : 0) + (hasAdvisors ? 20 : 0) + (hasPartners ? 20 : 0)
}

function calculateOfferingsCompletion(data: OfferingsData): number {
  const hasProducts = (data.products?.length || 0) > 0
  const hasServices = (data.services?.length || 0) > 0
  if (!hasProducts && !hasServices) return 0
  return hasProducts && hasServices ? 100 : 50
}

function calculateAudienceCompletion(data: AudienceData): number {
  const hasSegments = (data.segments?.length || 0) > 0
  const hasJourneys = (data.journeys?.length || 0) > 0
  return (hasSegments ? 60 : 0) + (hasJourneys ? 40 : 0)
}

function calculateBrandCompletion(data: BrandData): number {
  const fields = ["positioning_statement", "tone_of_voice", "brand_attributes"]
  const filled = fields.filter((f) => {
    const val = data[f as keyof BrandData]
    if (Array.isArray(val)) return val.length > 0
    return val && String(val).trim() !== ""
  }).length
  const hasVisual = Object.keys(data.visual || {}).length > 0
  const hasMessaging = Object.keys(data.messaging || {}).length > 0
  return Math.round((filled / fields.length) * 60 + (hasVisual ? 20 : 0) + (hasMessaging ? 20 : 0))
}

function calculateCompetitionCompletion(data: CompetitionData): number {
  const hasCompetitors = (data.competitors?.length || 0) > 0
  const hasSWOT = Object.values(data.swot || {}).some((arr) => arr && arr.length > 0)
  return (hasCompetitors ? 70 : 0) + (hasSWOT ? 30 : 0)
}

function calculateTechnologyCompletion(data: TechnologyData): number {
  const hasSystems = (data.systems?.length || 0) > 0
  const hasDataSources = (data.data_sources?.length || 0) > 0
  return (hasSystems ? 70 : 0) + (hasDataSources ? 30 : 0)
}

function calculateMarketingCompletion(data: MarketingData): number {
  const hasStrategy = !!data.strategy_summary
  const hasChannels = (data.channels?.length || 0) > 0
  const hasFunnels = (data.funnels?.length || 0) > 0
  return (hasStrategy ? 30 : 0) + (hasChannels ? 40 : 0) + (hasFunnels ? 30 : 0)
}

function calculateSalesCompletion(data: SalesData): number {
  const hasModel = !!data.sales_model
  const hasPipelines = (data.pipelines?.length || 0) > 0
  const hasPricing = Object.keys(data.pricing_overview || {}).length > 0
  return (hasModel ? 30 : 0) + (hasPipelines ? 40 : 0) + (hasPricing ? 30 : 0)
}

function calculateOperationsCompletion(data: OperationsData): number {
  const hasModel = !!data.operating_model
  const hasProcesses = (data.core_processes?.length || 0) > 0
  return (hasModel ? 40 : 0) + (hasProcesses ? 60 : 0)
}

function calculateFinanceCompletion(data: FinanceData): number {
  const hasRevModel = !!data.revenue_model
  const hasPricing = Object.keys(data.pricing_summary || {}).length > 0
  const hasUnitEcon = Object.keys(data.unit_economics || {}).length > 0
  return (hasRevModel ? 40 : 0) + (hasPricing ? 30 : 0) + (hasUnitEcon ? 30 : 0)
}

function calculateStrategyCompletion(data: StrategyData): number {
  const hasVision =
    !!data.strategic_vision?.three_year_vision || (data.strategic_vision?.one_year_objectives?.length || 0) > 0
  const hasGoals = (data.goals?.length || 0) > 0
  const hasRoadmaps = (data.roadmaps?.length || 0) > 0
  const hasPlans =
    (data.business_plans?.length || 0) > 0 ||
    (data.marketing_plans?.length || 0) > 0 ||
    (data.sales_plans?.length || 0) > 0
  const hasInitiatives = (data.strategic_initiatives?.length || 0) > 0
  return (
    (hasVision ? 25 : 0) +
    (hasGoals ? 30 : 0) +
    (hasRoadmaps ? 20 : 0) +
    (hasPlans ? 15 : 0) +
    (hasInitiatives ? 10 : 0)
  )
}

function calculateSectionCompletion(slug: SectionSlug, data: unknown): number {
  switch (slug) {
    case "identity":
      return calculateIdentityCompletion(data as IdentityData)
    case "people":
      return calculatePeopleCompletion(data as PeopleData)
    case "offerings":
      return calculateOfferingsCompletion(data as OfferingsData)
    case "audience":
      return calculateAudienceCompletion(data as AudienceData)
    case "brand":
      return calculateBrandCompletion(data as BrandData)
    case "competition":
      return calculateCompetitionCompletion(data as CompetitionData)
    case "technology":
      return calculateTechnologyCompletion(data as TechnologyData)
    case "marketing":
      return calculateMarketingCompletion(data as MarketingData)
    case "sales":
      return calculateSalesCompletion(data as SalesData)
    case "operations":
      return calculateOperationsCompletion(data as OperationsData)
    case "finance":
      return calculateFinanceCompletion(data as FinanceData)
    case "strategy":
      return calculateStrategyCompletion(data as StrategyData)
    default:
      return 0
  }
}

function getCompletionStatus(percent: number): CompletionStatus {
  if (percent === 0) return "not_started"
  if (percent >= 100) return "complete"
  return "in_progress"
}

// ============================================
// INITIALIZE ALL 12 SECTIONS
// ============================================
function createInitialSections(): SectionState[] {
  return SECTION_ORDER.map((slug, index) => {
    const config = SECTION_CONFIGS[slug]
    return {
      id: slug,
      slug,
      label: config.label,
      description: config.description,
      icon: config.icon,
      data: defaultSectionData[slug],
      completionStatus: "not_started" as CompletionStatus,
      completionPercent: 0,
      displayOrder: index + 1,
      estimatedTime: config.estimatedTime,
    }
  })
}

// ============================================
// STORE STATE
// ============================================
interface KnowledgeState {
  sections: SectionState[]
  totalCompletion: number
  activeSectionSlug: SectionSlug | null
}

let state: KnowledgeState = {
  sections: createInitialSections(),
  totalCompletion: 0,
  activeSectionSlug: null,
}

const listeners = new Set<() => void>()

function emitChange() {
  listeners.forEach((listener) => listener())
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function getSnapshot() {
  return state
}

function getServerSnapshot(): KnowledgeState {
  return {
    sections: createInitialSections(),
    totalCompletion: 0,
    activeSectionSlug: null,
  }
}

// ============================================
// ACTIONS
// ============================================
function getSectionBySlug(slug: SectionSlug) {
  return state.sections.find((s) => s.slug === slug)
}

function updateSectionData<T extends SectionSlug>(slug: T, partialData: Partial<SectionDataMap[T]>) {
  const updatedSections = state.sections.map((section) => {
    if (section.slug !== slug) return section
    const newData = { ...section.data, ...partialData }
    const completionPercent = calculateSectionCompletion(slug, newData)
    return {
      ...section,
      data: newData,
      completionPercent,
      completionStatus: getCompletionStatus(completionPercent),
      lastUpdated: new Date().toISOString(),
    }
  })
  const totalCompletion = Math.round(
    updatedSections.reduce((acc, s) => acc + s.completionPercent, 0) / updatedSections.length,
  )
  state = { ...state, sections: updatedSections, totalCompletion }
  emitChange()
}

function setSectionData<T extends SectionSlug>(slug: T, data: SectionDataMap[T]) {
  const updatedSections = state.sections.map((section) => {
    if (section.slug !== slug) return section
    const completionPercent = calculateSectionCompletion(slug, data)
    return {
      ...section,
      data,
      completionPercent,
      completionStatus: getCompletionStatus(completionPercent),
      lastUpdated: new Date().toISOString(),
    }
  })
  const totalCompletion = Math.round(
    updatedSections.reduce((acc, s) => acc + s.completionPercent, 0) / updatedSections.length,
  )
  state = { ...state, sections: updatedSections, totalCompletion }
  emitChange()
}

function setActiveSection(slug: SectionSlug | null) {
  state = { ...state, activeSectionSlug: slug }
  emitChange()
}

function getNextIncompleteSection(): SectionSlug | null {
  const incomplete = state.sections.find((s) => s.completionStatus !== "complete")
  return incomplete?.slug || null
}

// Products
function addProduct(product: OfferingsData["products"][number]) {
  const section = getSectionBySlug("offerings")
  if (!section) return
  const data = section.data as OfferingsData
  const products = [...(data.products || []), { ...product, id: product.id || crypto.randomUUID() }]
  updateSectionData("offerings", { products })
}

function updateProduct(productId: string, updates: Partial<OfferingsData["products"][number]>) {
  const section = getSectionBySlug("offerings")
  if (!section) return
  const data = section.data as OfferingsData
  const products = (data.products || []).map((p) => (p.id === productId ? { ...p, ...updates } : p))
  updateSectionData("offerings", { products })
}

function removeProduct(productId: string) {
  const section = getSectionBySlug("offerings")
  if (!section) return
  const data = section.data as OfferingsData
  const products = (data.products || []).filter((p) => p.id !== productId)
  updateSectionData("offerings", { products })
}

// Services
function addService(service: OfferingsData["services"][number]) {
  const section = getSectionBySlug("offerings")
  if (!section) return
  const data = section.data as OfferingsData
  const services = [...(data.services || []), { ...service, id: service.id || crypto.randomUUID() }]
  updateSectionData("offerings", { services })
}

function updateService(serviceId: string, updates: Partial<OfferingsData["services"][number]>) {
  const section = getSectionBySlug("offerings")
  if (!section) return
  const data = section.data as OfferingsData
  const services = (data.services || []).map((s) => (s.id === serviceId ? { ...s, ...updates } : s))
  updateSectionData("offerings", { services })
}

function removeService(serviceId: string) {
  const section = getSectionBySlug("offerings")
  if (!section) return
  const data = section.data as OfferingsData
  const services = (data.services || []).filter((s) => s.id !== serviceId)
  updateSectionData("offerings", { services })
}

// Segments
function addSegment(segment: AudienceData["segments"][number]) {
  const section = getSectionBySlug("audience")
  if (!section) return
  const data = section.data as AudienceData
  const segments = [...(data.segments || []), { ...segment, id: segment.id || crypto.randomUUID() }]
  updateSectionData("audience", { segments })
}

function updateSegment(segmentId: string, updates: Partial<AudienceData["segments"][number]>) {
  const section = getSectionBySlug("audience")
  if (!section) return
  const data = section.data as AudienceData
  const segments = (data.segments || []).map((s) => (s.id === segmentId ? { ...s, ...updates } : s))
  updateSectionData("audience", { segments })
}

function removeSegment(segmentId: string) {
  const section = getSectionBySlug("audience")
  if (!section) return
  const data = section.data as AudienceData
  const segments = (data.segments || []).filter((s) => s.id !== segmentId)
  updateSectionData("audience", { segments })
}

// Team members
function addTeamMember(member: PeopleData["team_members"][number]) {
  const section = getSectionBySlug("people")
  if (!section) return
  const data = section.data as PeopleData
  const team_members = [...(data.team_members || []), { ...member, id: member.id || crypto.randomUUID() }]
  updateSectionData("people", { team_members })
}

function updateTeamMember(memberId: string, updates: Partial<PeopleData["team_members"][number]>) {
  const section = getSectionBySlug("people")
  if (!section) return
  const data = section.data as PeopleData
  const team_members = (data.team_members || []).map((m) => (m.id === memberId ? { ...m, ...updates } : m))
  updateSectionData("people", { team_members })
}

function removeTeamMember(memberId: string) {
  const section = getSectionBySlug("people")
  if (!section) return
  const data = section.data as PeopleData
  const team_members = (data.team_members || []).filter((m) => m.id !== memberId)
  updateSectionData("people", { team_members })
}

// Competitors
function addCompetitor(competitor: CompetitionData["competitors"][number]) {
  const section = getSectionBySlug("competition")
  if (!section) return
  const data = section.data as CompetitionData
  const competitors = [...(data.competitors || []), { ...competitor, id: competitor.id || crypto.randomUUID() }]
  updateSectionData("competition", { competitors })
}

function updateCompetitor(competitorId: string, updates: Partial<CompetitionData["competitors"][number]>) {
  const section = getSectionBySlug("competition")
  if (!section) return
  const data = section.data as CompetitionData
  const competitors = (data.competitors || []).map((c) => (c.id === competitorId ? { ...c, ...updates } : c))
  updateSectionData("competition", { competitors })
}

function removeCompetitor(competitorId: string) {
  const section = getSectionBySlug("competition")
  if (!section) return
  const data = section.data as CompetitionData
  const competitors = (data.competitors || []).filter((c) => c.id !== competitorId)
  updateSectionData("competition", { competitors })
}

function resetAllSections() {
  state = {
    sections: createInitialSections(),
    totalCompletion: 0,
    activeSectionSlug: null,
  }
  emitChange()
}

function loadFromServer(sections: SectionState[]) {
  const totalCompletion = Math.round(sections.reduce((acc, s) => acc + s.completionPercent, 0) / sections.length)
  state = { ...state, sections, totalCompletion }
  emitChange()
}

function exportForMigration(): Record<SectionSlug, unknown> {
  return state.sections.reduce(
    (acc, section) => {
      acc[section.slug] = section.data
      return acc
    },
    {} as Record<SectionSlug, unknown>,
  )
}

// ============================================
// HOOK
// ============================================
export function useKnowledgeStore() {
  const currentState = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)

  return {
    sections: currentState.sections,
    totalCompletion: currentState.totalCompletion,
    activeSectionSlug: currentState.activeSectionSlug,
    getSectionBySlug: useCallback(getSectionBySlug, []),
    updateSectionData: useCallback(updateSectionData, []),
    setSectionData: useCallback(setSectionData, []),
    setActiveSection: useCallback(setActiveSection, []),
    getNextIncompleteSection: useCallback(getNextIncompleteSection, []),
    addProduct: useCallback(addProduct, []),
    updateProduct: useCallback(updateProduct, []),
    removeProduct: useCallback(removeProduct, []),
    addService: useCallback(addService, []),
    updateService: useCallback(updateService, []),
    removeService: useCallback(removeService, []),
    addSegment: useCallback(addSegment, []),
    updateSegment: useCallback(updateSegment, []),
    removeSegment: useCallback(removeSegment, []),
    addTeamMember: useCallback(addTeamMember, []),
    updateTeamMember: useCallback(updateTeamMember, []),
    removeTeamMember: useCallback(removeTeamMember, []),
    addCompetitor: useCallback(addCompetitor, []),
    updateCompetitor: useCallback(updateCompetitor, []),
    removeCompetitor: useCallback(removeCompetitor, []),
    resetAllSections: useCallback(resetAllSections, []),
    loadFromServer: useCallback(loadFromServer, []),
    exportForMigration: useCallback(exportForMigration, []),
  }
}

// ============================================
// LEGACY EXPORTS FOR BACKWARDS COMPATIBILITY
// ============================================
export interface KnowledgeSection {
  id: string
  title: string
  description: string
  completionPercent: number
  fields: string[]
  data?: Record<string, string>
  isMultiItem?: boolean
  items?: unknown[]
  minItems?: number
}

export const knowledgeSections: KnowledgeSection[] = SECTION_ORDER.map((slug) => {
  const config = SECTION_CONFIGS[slug]
  return {
    id: slug,
    title: config.label,
    description: config.description,
    completionPercent: 0,
    fields: config.requiredFields,
  }
})

export function createEmptyProduct() {
  return {
    id: crypto.randomUUID(),
    name: "",
    short_description: "",
    detailed_description: "",
    pricing: { amount: 0, currency: "USD", type: "one_time" as const },
    target_customer: "",
    key_benefits: [],
    category: "other" as const,
    status: "active" as const,
  }
}

export function createEmptyService() {
  return {
    id: crypto.randomUUID(),
    name: "",
    short_description: "",
    service_description: "",
    pricing_model: "project" as const,
    price: 0,
    price_unit: "project",
    typical_duration: "",
    key_deliverables: [],
    ideal_client_profile: "",
    category: "consulting" as const,
  }
}

// Placeholder API functions
export async function getKnowledgeProfile(tenantId: string) {
  return {
    sections: state.sections,
    totalCompletion: state.totalCompletion,
  }
}

export async function saveKnowledgeSection(sectionId: string, data: Record<string, unknown>) {
  console.log(`Saving section ${sectionId}:`, data)
  return { success: true }
}
